if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        // Keep slide/thumbnail in sync
        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        // Click to switch media
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        // NEW: Preload on thumbnail hover (desktop / precise pointer)
        const canHover = matchMedia('(hover:hover) and (pointer:fine)').matches;
        if (canHover) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((node) => {
            node.addEventListener('mouseenter', () => {
              const targetId = node.dataset.target;
              const el = this.elements.viewer.querySelector(`[data-media-id="${targetId}"]`);
              const img = el && el.querySelector('img');
              if (!img) return;
              if (img.classList.contains('is-loaded')) return;

              // Warm the request without stealing bandwidth from current image
              try { if ('fetchPriority' in img) img.fetchPriority = 'low'; } catch (e) {}
              // Kick the decode pipeline; when done, mark as loaded for instant fade later
              if (img.decode) {
                img.decode().then(() => img.classList.add('is-loaded')).catch(() => {});
              } else if (img.complete && img.naturalWidth) {
                img.classList.add('is-loaded');
              } else {
                img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
              }
            });
          });
        }

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        if (thumbnail) this.setActiveThumbnail(thumbnail);
      }

      // NEW: helper — resolve when an <img> is ready to paint, then mark it loaded
      waitForImageReady(img) {
        return new Promise((resolve) => {
          // Ensure the browser actually prioritizes this image
          try {
            if (img.loading === 'lazy') img.loading = 'eager';
            if ('fetchPriority' in img) img.fetchPriority = 'high';
          } catch (e) {}

          const done = () => {
            img.classList.add('is-loaded');
            resolve();
          };

          // If already loaded from cache
          if (img.complete && img.naturalWidth) {
            return done();
          }

          // Use decode() when available
          if (img.decode) {
            img.decode().then(done).catch(() => {
              // Fallback to load event
              img.addEventListener('load', done, { once: true });
            });
          } else {
            img.addEventListener('load', done, { once: true });
          }
        });
      }

      // UPDATED: async — wait for next image to be ready, then swap (CSS fades .is-active img.is-loaded)
      async setActiveMedia(mediaId, prepend) {
        const next =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!next) return;

        // If the target slide has an image, wait until it can paint cleanly
        const nextImg = next.querySelector('img');
        if (nextImg && !nextImg.classList.contains('is-loaded')) {
          try { await this.waitForImageReady(nextImg); } catch (e) {}
        }

        // Pause any playing autoplay-on-select videos
        this.elements.viewer.querySelectorAll('deferred-media[data-autoplay-on-select]').forEach((dm) => {
          dm.pauseAutoplay();
        });

        // Swap active state
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        next.classList.add('is-active');

        // If the new slide has an autoplay-on-select video, play it from start
        const autoplayMedia = next.querySelector('deferred-media[data-autoplay-on-select]');
        if (autoplayMedia) autoplayMedia.playFromStart();

        // ✅ keep thumbnails in sync
        if (this.elements.thumbnails) {
          const activeThumbnail =
            this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
        }

        // Notify header color detection that visible media changed
        document.dispatchEvent(new CustomEvent('media:changed'));
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) {
          var videoElem = deferredMedia.querySelector('video');
          if (videoElem) {
            videoElem.play();
          }
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}